import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FlaskConical, Leaf, Loader2, Plus, Scale } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import soilService from '../../services/soil';
import orchardService from '../../services/orchard';
import { errorMessage } from '../../utils/apiResponse';
import { formatLocalDateInTz, useOrgTimezone } from '../../utils/timezone';
import { ANALYTE_BY_KEY, depthText, fmtNum, showValue } from '../../utils/soilAnalytes';
import { cn } from '../../utils/cn';
import type { Farm } from '../../types/orchard';
import type { SampleType, SoilSample } from '../../types/soil';

const COLUMNS: Record<SampleType, string[]> = {
  soil: ['ph_cacl2', 'phosphorus_colwell', 'potassium_colwell', 'organic_carbon'],
  leaf: ['leaf_n', 'leaf_p', 'leaf_k', 'leaf_ca', 'leaf_b'],
};

/**
 * Every soil sample of the organisation, newest first, by farm. A row opens
 * the sample; the block and cultivar area pages show their own.
 */
export default function SoilAnalysis() {
  const navigate = useNavigate();
  const timezone = useOrgTimezone();
  const [params, setParams] = useSearchParams();
  const farmId = params.get('farm_id') ?? '';
  const blockId = params.get('block_id') ?? '';
  const typeParam = params.get('type');
  const type: SampleType | null = typeParam === 'leaf' || typeParam === 'soil' ? typeParam : null;
  const [farms, setFarms] = useState<Farm[]>([]);
  const [samples, setSamples] = useState<SoilSample[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    orchardService.getAllFarms().then((r) => { if (r.success && r.data) setFarms(r.data.farms); }).catch(() => {});
  }, []);

  useEffect(() => {
    setSamples(null);
    soilService.list({ farm_id: farmId ? Number(farmId) : undefined, block_id: blockId ? Number(blockId) : undefined, sample_type: type ?? undefined })
      .then((r) => setSamples(r.success && r.data ? r.data : []))
      .catch((e) => setError(errorMessage(e, 'Could not load the soil samples')));
  }, [farmId, blockId, type]);

  const shown = useMemo(
    () => [...COLUMNS.soil, ...COLUMNS.leaf].filter((k) => samples?.some((s) => s.results.some((r) => r.analyte === k))),
    [samples],
  );
  const setFilter = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Soil and leaf analysis"
        subtitle="Lab results for your blocks and plantings"
        icon={<FlaskConical className="h-6 w-6 text-white" />}
        showBackButton
        actionButtons={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/orchard/soil/removal')}>
              <Scale className="h-4 w-4" />
              Crop figures
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/orchard/soil/new?type=leaf')}>
              <Leaf className="h-4 w-4" />
              Leaf sample
            </Button>
            <Button variant="primary" size="sm" onClick={() => navigate('/orchard/soil/new')}>
              <Plus className="h-4 w-4" />
              Soil sample
            </Button>
          </div>
        }
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <label className="block text-sm w-full max-w-xs">
            <span className="text-neutral-700">Farm</span>
            <select
              className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm"
              value={farmId}
              onChange={(e) => setFilter('farm_id', e.target.value)}
            >
              <option value="">Every farm</option>
              {farms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </label>
          <div className="flex gap-1" role="radiogroup" aria-label="Kind of sample">
            {([['', 'All'], ['soil', 'Soil'], ['leaf', 'Leaf']] as const).map(([v, label]) => (
              <button
                key={v}
                type="button"
                role="radio"
                aria-checked={(type ?? '') === v}
                onClick={() => setFilter('type', v)}
                className={cn('px-3 py-1.5 rounded-full border text-sm',
                  (type ?? '') === v ? 'bg-primary-50 border-primary-300 text-primary-800' : 'border-neutral-200 text-neutral-600 bg-white')}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {error && <p className="text-sm text-error-700">{error}</p>}
        {!samples && !error && <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-neutral-400" /></div>}
        {samples && samples.length === 0 && (
          <div className="bg-white rounded-xl border border-neutral-200 p-8 text-center space-y-2">
            <p className="text-sm text-neutral-700">No samples yet.</p>
            <p className="text-sm text-neutral-500">Upload a lab report and its figures are read into the form for you to check, or type them in.</p>
          </div>
        )}
        {samples && samples.length > 0 && (
          <ul className="space-y-2">
            {samples.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => navigate(`/orchard/soil/${s.id}`)}
                  className="w-full text-left bg-white rounded-xl border border-neutral-200 p-4 hover:border-primary-300 space-y-1"
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                    <p className="text-sm font-medium text-neutral-900 break-words min-w-0">
                      {s.sample_type === 'leaf' && (
                        <span className="inline-flex items-center gap-1 mr-1.5 text-xs font-normal text-green-800 bg-green-50 border border-green-200 rounded px-1.5 py-0.5 align-middle">
                          <Leaf className="h-3 w-3" />Leaf
                        </span>
                      )}
                      {s.farm_name} › {s.block_name}{s.cultivar_area_name ? ` › ${s.cultivar_area_name}` : ''}
                    </p>
                    <p className="text-xs text-neutral-500">
                      {formatLocalDateInTz(s.sampled_on, timezone)}
                      {depthText(s.depth_from_cm, s.depth_to_cm) && ` · ${depthText(s.depth_from_cm, s.depth_to_cm)}`}
                      {s.lab_name && ` · ${s.lab_name}`}
                    </p>
                  </div>
                  <p className="text-xs text-neutral-600 flex flex-wrap gap-x-3 gap-y-1">
                    {shown.filter((k) => COLUMNS[s.sample_type ?? 'soil'].includes(k)).map((k) => {
                      const r = s.results.find((x) => x.analyte === k);
                      return r ? <span key={k}>{ANALYTE_BY_KEY.get(k)?.label} <strong className="text-neutral-900">{showValue(r)}</strong>{s.sample_type === 'leaf' && r.unit ? ` ${r.unit}` : ''}</span> : null;
                    })}
                    {s.derived.ecec != null && <span>ECEC <strong className="text-neutral-900">{fmtNum(s.derived.ecec)}</strong></span>}
                    <span className="text-neutral-400">{s.results.length} results</span>
                  </p>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
